/**
 * Wire gauge to weight-per-foot lookup (lbs/LF).
 * Source: standard steel wire gauge tables.
 * Cross-section area × steel density (0.2833 lb/in³) × 12 in/ft.
 */
const GAUGE_TABLE = {
    0: 0.255,
    1: 0.221,
    2: 0.187,
    3: 0.162,
    4: 0.136,
    5: 0.117,
    6: 0.098,
    7: 0.083,
    8: 0.071,
    9: 0.059,
    10: 0.049,
    11: 0.041,
    12: 0.031,
};
export function wireWeightPerFoot(gauge) {
    const wpf = GAUGE_TABLE[gauge];
    if (wpf === undefined) {
        throw new Error(`Unknown wire gauge: ${gauge}. Supported: ${Object.keys(GAUGE_TABLE).join(', ')}`);
    }
    return wpf;
}
/**
 * Parse mesh spec string: "6x6 10Ga 8x20"
 *   grid: 6"×6", wire: 10 gauge, dimensions: 8'×20'
 * Sheets are <= 40' long, rolls are > 40'.
 */
export function parseMeshSpec(input) {
    const parts = input.trim().split(/\s+/);
    if (parts.length !== 3) {
        throw new Error(`Invalid mesh spec: "${input}". Expected "gridXxY gaugeGa widthXlength".`);
    }
    const [gridPart, gaugePart, dimPart] = parts;
    const gridMatch = gridPart.match(/^(\d+)x(\d+)$/i);
    if (!gridMatch)
        throw new Error(`Invalid grid spacing: "${gridPart}".`);
    const gridSpacingX = parseInt(gridMatch[1], 10);
    const gridSpacingY = parseInt(gridMatch[2], 10);
    const gaugeMatch = gaugePart.match(/^(\d+)Ga$/i);
    if (!gaugeMatch)
        throw new Error(`Invalid gauge: "${gaugePart}".`);
    const gauge = parseInt(gaugeMatch[1], 10);
    const dimMatch = dimPart.match(/^(\d+)x(\d+)$/i);
    if (!dimMatch)
        throw new Error(`Invalid dimensions: "${dimPart}".`);
    const width = parseInt(dimMatch[1], 10);
    const length = parseInt(dimMatch[2], 10);
    const unit = length > 40 ? 'roll' : 'sheet';
    return { gridSpacingX, gridSpacingY, gauge, width, length, unit };
}
/**
 * Compute weight of one mesh sheet or roll (lbs).
 * Counts wires in each direction based on grid spacing and dimensions,
 * then multiplies total wire length by weight-per-foot.
 */
export function meshWeight(spec) {
    const wpf = wireWeightPerFoot(spec.gauge);
    // Wires running along the width (spaced by gridSpacingY along the length)
    const wiresAlongWidth = Math.floor((spec.length * 12) / spec.gridSpacingY) + 1;
    const lengthOfWidthWires = wiresAlongWidth * spec.width; // LF
    // Wires running along the length (spaced by gridSpacingX along the width)
    const wiresAlongLength = Math.floor((spec.width * 12) / spec.gridSpacingX) + 1;
    const lengthOfLengthWires = wiresAlongLength * spec.length; // LF
    return (lengthOfWidthWires + lengthOfLengthWires) * wpf;
}
